import { AppShell } from "@mantine/core";
import type { ReactNode } from "react";
import { AndroidDownloadModal } from "./AndroidDownloadModal";

/**
 * The page: header, content, timeline.
 *
 * `mode="static"` is the whole reason this fits: it drops the fixed positioning and lays the
 * three slots out as real grid rows. The `styles` overrides below are inline, so they beat
 * AppShell's own class rules; each one is listed with its reason here.
 *
 *   root rows      `1fr` is `minmax(auto, 1fr)`, and the middle row has to be allowed below
 *                  its content or the page scrolls instead of the panels shrinking.
 *   root columns   one column that may be narrower than its contents -- the timeline canvas
 *                  carries an inline pixel width, and an implicit `auto` column deadlocks
 *                  against it when the window shrinks.
 *   root overflow  below the 469px floor the *page* scrolls, not the shell.
 *   header/footer  both bands are content-sized grid rows, and the timeline's height is
 *                  computed in JS per chat-lane state.
 *
 * No `header` / `footer` config prop: those exist to publish a declared height as
 * `--app-shell-header-height`, and both bands here size themselves. The `height: auto`
 * override is what makes that true rather than relying on an unset variable.
 *
 * 469 is measured, not derived: where the stacked layout's transport stops reaching the
 * canvas. `index.html` sets `viewport-fit=cover`, so the side insets are ours to clear; they
 * come from `theme.spacing`, which is where the `env()` expressions live. The top inset is on
 * the header's inner `Group` instead, so the band's background fills the notch strip.
 */
export function Shell({ children }: { children: ReactNode }) {
  return (
    <AppShell
      mode="static"
      withBorder={false}
      offsetScrollbars={false}
      h="100dvh"
      mih={469}
      pl="safeLeft"
      pr="safeRight"
      styles={{
        root: {
          gridTemplateRows: "auto minmax(0, 1fr) auto",
          gridTemplateColumns: "minmax(0, 1fr)",
          overflow: "visible",
        },
        // AppShell already paints `--mantine-color-body`, so only the rule and the ink have
        // to be said, and both are Mantine's own variables.
        header: {
          gridRow: 1,
          position: "static",
          height: "auto",
          borderBottom: "1px solid var(--mantine-color-default-border)",
          color: "var(--mantine-color-text)",
        },
        main: { gridRow: 2, minHeight: 0, overflow: "auto", position: "relative" },
        // The native activity publishes the same bottom inset token used by the other edges.
        // Browser builds fall back to the CSS safe-area environment value in the theme.
        footer: {
          gridRow: 3,
          position: "static",
          height: "auto",
          borderTop: "1px solid var(--mantine-color-default-border)",
          background: "var(--mantine-color-sunken-filled)",
          color: "var(--mantine-color-text)",
          paddingBottom: "var(--mantine-spacing-safeBottom)",
        },
      }}
    >
      {children}
      <AndroidDownloadModal />
    </AppShell>
  );
}

Shell.Header = AppShell.Header;
Shell.Main = AppShell.Main;
Shell.Footer = AppShell.Footer;
